/**
 * ActivityTiles — W55 "Today's Drivers · Activity" band.
 *
 * Three sparkline tiles: TOUCHES / ENGAGEMENTS / MQLS CREATED
 *
 * Data sources:
 *  - TOUCHES: cadence_step_completions rows (tenant_id join via cadence_enrollments)
 *    rolling 30 days, daily counts for sparkline.
 *  - ENGAGEMENTS: subset of touches with outcome IN ('replied','booked','interested')
 *  - MQLS CREATED: accounts added in the last 30 days with status Suspect+
 *    (proxy; no standalone mql table — documented W55 gap).
 *
 * Tenant 1 only. SIM excluded.
 */

import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";
import {
  MQL_TARGET_BY_QUARTER,
  MQL_TARGET_ANNUAL,
  ENGAGEMENT_RATE_TARGET_PCT,
} from "@/data/marketing-targets";

const TENANT_ID = 1;

function getCurrentQuarter(): 1 | 2 | 3 | 4 {
  const m = new Date().getMonth() + 1;
  if (m <= 3) return 1;
  if (m <= 6) return 2;
  if (m <= 9) return 3;
  return 4;
}

function last30DaysBuckets(): { date: string; label: string }[] {
  const buckets: { date: string; label: string }[] = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const date = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
    buckets.push({ date, label });
  }
  return buckets;
}

interface SparklineProps {
  data: number[];
  color: string;
  height?: number;
}

function Sparkline({ data, color, height = 32 }: SparklineProps) {
  if (!data.length) return <div style={{ height }} className="w-full" />;
  const max = Math.max(...data, 1);
  const w = 120;
  const h = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / max) * h;
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none">
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface TileData {
  total: number;
  daily: number[];
  rate?: number;
  rateTarget?: number;
  quarterTarget?: number;
  annualTarget?: number;
}

export function ActivityTiles() {
  const [touches, setTouches] = useState<TileData | null>(null);
  const [engagements, setEngagements] = useState<TileData | null>(null);
  const [mqls, setMqls] = useState<TileData | null>(null);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();

  useEffect(() => {
    const tenantId = tenant?.tenantId ?? TENANT_ID;
    let cancelled = false;
    setLoading(true);

    const buckets = last30DaysBuckets();
    const startDate = buckets[0].date;
    const q = getCurrentQuarter();
    const qTarget = MQL_TARGET_BY_QUARTER[q];

    Promise.all([
      // Touches: cadence_step_completions — join to cadence_enrollments for tenant_id
      supabase()
        .from("cadence_step_completions")
        .select(`
          completed_at,
          outcome,
          cadence_enrollments!inner(tenant_id)
        `)
        .gte("completed_at", startDate + "T00:00:00Z")
        .eq("cadence_enrollments.tenant_id", tenantId),

      // MQL proxy: accounts with Suspect+ status, created in last 30 days
      supabase()
        .from("accounts")
        .select("id, created_at")
        .eq("tenant_id", tenantId)
        .in("status", ["Suspect", "Prospect", "Qualified Opp", "Full Opportunity"])
        .gte("created_at", startDate + "T00:00:00Z")
        .not("name", "ilike", "[SIM-%")
        .not("name", "ilike", "SMOKE_TEST_%"),
    ]).then(([touchRes, mqlRes]) => {
      if (cancelled) return;

      // Build daily buckets
      const dateMap = Object.fromEntries(buckets.map(b => [b.date, 0]));
      const engDateMap = Object.fromEntries(buckets.map(b => [b.date, 0]));
      const mqlDateMap = Object.fromEntries(buckets.map(b => [b.date, 0]));

      const ENGAGED_OUTCOMES = new Set(["replied", "booked", "interested", "meeting_booked", "responded"]);

      let totalTouches = 0;
      let totalEngaged = 0;
      for (const r of (touchRes.data ?? [])) {
        const d = (r.completed_at as string)?.slice(0, 10);
        if (d && d in dateMap) {
          dateMap[d]++;
          totalTouches++;
          if (r.outcome && ENGAGED_OUTCOMES.has(String(r.outcome).toLowerCase())) {
            engDateMap[d]++;
            totalEngaged++;
          }
        }
      }

      let totalMqls = 0;
      for (const a of (mqlRes.data ?? [])) {
        const d = (a.created_at as string)?.slice(0, 10);
        if (d && d in mqlDateMap) {
          mqlDateMap[d]++;
          totalMqls++;
        }
      }

      const dailyTouches = buckets.map(b => dateMap[b.date]);
      const dailyEng = buckets.map(b => engDateMap[b.date]);
      const dailyMqls = buckets.map(b => mqlDateMap[b.date]);

      const engagementRate = totalTouches > 0 ? Math.round((totalEngaged / totalTouches) * 100) : 0;

      setTouches({ total: totalTouches, daily: dailyTouches });
      setEngagements({
        total: totalEngaged,
        daily: dailyEng,
        rate: engagementRate,
        rateTarget: ENGAGEMENT_RATE_TARGET_PCT,
      });
      setMqls({
        total: totalMqls,
        daily: dailyMqls,
        quarterTarget: qTarget,
        annualTarget: MQL_TARGET_ANNUAL,
      });

      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [tenant?.tenantId]);

  const q = getCurrentQuarter();
  const quarterName = ["Q1", "Q2", "Q3", "Q4"][q - 1];

  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[1,2,3].map(i => (
          <div key={i} className="rounded-lg border border-neutral-200 bg-white p-4 animate-pulse">
            <div className="h-3 w-24 bg-neutral-100 rounded mb-4" />
            <div className="h-8 w-16 bg-neutral-100 rounded mb-3" />
            <div className="h-8 w-full bg-neutral-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {/* TOUCHES */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500">Touches</div>
        <div className="text-3xl font-bold tabular-nums" style={{ color: "#0B0B0F" }}>
          {touches?.total ?? 0}
        </div>
        <div className="text-[11px] text-neutral-400 mb-2">daily volume — last 30 days</div>
        <Sparkline data={touches?.daily ?? []} color="#0B0B0F" />
      </div>

      {/* ENGAGEMENTS */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500">Engagements</div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold tabular-nums" style={{ color: "#0B0B0F" }}>
            {engagements?.total ?? 0}
          </span>
          {engagements?.rate != null && (
            <span
              className="text-base font-semibold tabular-nums"
              style={{ color: (engagements.rate ?? 0) >= ENGAGEMENT_RATE_TARGET_PCT ? "#15803D" : "#DC2626" }}
            >
              {engagements.rate}%
            </span>
          )}
        </div>
        <div className="text-[11px] text-neutral-400 mb-2">
          engagement rate &middot; target {ENGAGEMENT_RATE_TARGET_PCT}%
        </div>
        <Sparkline data={engagements?.daily ?? []} color="#7C3AED" />
      </div>

      {/* MQLS CREATED */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500">MQLs Created</div>
        <div className="text-3xl font-bold tabular-nums" style={{ color: "#0B0B0F" }}>
          {mqls?.total ?? 0}
        </div>
        <div className="text-[11px] text-neutral-400 mb-2">
          Target {mqls?.quarterTarget ?? 0} {quarterName} / {mqls?.annualTarget ?? 0} annual
        </div>
        <Sparkline data={mqls?.daily ?? []} color="#DC2626" />
      </div>
    </div>
  );
}
